import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, Cpu, Database, Activity, RefreshCcw, PlayCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminOverview() {
    const [ov, setOv] = useState(null);
    const load = () => api.get("/admin/overview").then((r) => setOv(r.data));
    useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

    const seedJobs = async () => {
        try {
            const r = await api.post("/admin/jobs/seed");
            toast.success(`Seeded ${r.data.created} generation jobs`);
            load();
        } catch (e) { toast.error("Failed to seed jobs"); }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
            <div className="flex items-center justify-between">
                <div>
                    <span className="overline text-primary">▮ Admin</span>
                    <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Platform Overview</h1>
                </div>
                <div className="flex gap-2">
                    <Button data-testid="admin-refresh-btn" variant="outline" className="rounded-sm btn-press" onClick={load}>
                        <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                    <Button data-testid="admin-seed-jobs-btn" className="rounded-sm bg-primary hover:bg-primary/90 btn-press" onClick={seedJobs}>
                        <PlayCircle className="w-4 h-4 mr-2" /> Seed jobs
                    </Button>
                </div>
            </div>

            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in">
                <Card icon={<Users />} label="Users" value={ov?.users_total ?? "—"} sub={`${ov?.students ?? 0} students`} />
                <Card icon={<Activity />} label="Sessions" value={ov?.sessions ?? "—"} sub={`${ov?.attempts ?? 0} completed`} accent />
                <Card icon={<Cpu />} label="Workers" value={ov?.workers ?? "—"} sub={`${ov?.online_workers ?? 0} online`} />
                <Card icon={<Database />} label="Vector DB" value={ov?.vector_db?.total ?? "—"} sub="questions in bank" />
            </div>

            <div className="mt-10 grid lg:grid-cols-2 gap-4">
                <div className="border border-border rounded-sm p-6">
                    <h2 className="font-heading font-bold text-xl">Jobs</h2>
                    <div className="mt-4 grid grid-cols-2 gap-3 font-mono">
                        <div className="border border-border rounded-sm p-4">
                            <div className="overline text-muted-foreground">Pending</div>
                            <div className="font-heading font-black text-3xl mt-2">{ov?.jobs_pending ?? 0}</div>
                        </div>
                        <div className="border border-green-600/60 rounded-sm p-4">
                            <div className="overline text-green-600">Completed</div>
                            <div className="font-heading font-black text-3xl mt-2">{ov?.jobs_completed ?? 0}</div>
                        </div>
                    </div>
                </div>

                <div className="border border-border rounded-sm p-6">
                    <h2 className="font-heading font-bold text-xl">Vector DB · by exam</h2>
                    <div className="mt-4 grid grid-cols-2 gap-3 font-mono">
                        <div className="border border-border rounded-sm p-4">
                            <div className="overline text-muted-foreground">NEET</div>
                            <div className="font-heading font-black text-3xl mt-2">{ov?.vector_db?.by_exam?.NEET ?? 0}</div>
                        </div>
                        <div className="border border-border rounded-sm p-4">
                            <div className="overline text-muted-foreground">JEE</div>
                            <div className="font-heading font-black text-3xl mt-2">{ov?.vector_db?.by_exam?.JEE ?? 0}</div>
                        </div>
                    </div>
                    <div className="mt-4 text-xs font-mono">
                        {Object.entries(ov?.vector_db?.by_subject || {}).map(([k, v]) => (
                            <div key={k} className="flex justify-between border-b border-border py-1">
                                <span className="text-muted-foreground">{k}</span>
                                <span>{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const Card = ({ icon, label, value, sub, accent }) => (
    <div className="border border-border rounded-sm p-5">
        <div className="flex justify-between items-start">
            <div>
                <div className="overline text-muted-foreground">{label}</div>
                <div className={`font-heading font-black text-4xl mt-2 font-mono ${accent ? "text-primary" : ""}`}>{value}</div>
                <div className="text-xs text-muted-foreground mt-1">{sub}</div>
            </div>
            <div className="text-primary">{React.cloneElement(icon, { className: "w-5 h-5" })}</div>
        </div>
    </div>
);
